/**
 * Retry Utility
 *
 * Provides exponential backoff retry logic for network operations.
 * Used for wallet API calls that may fail due to transient network issues.
 *
 * @module utils/retry
 */

import { MAX_RETRY_ATTEMPTS, RETRY_BASE_DELAY_MS, MAX_RETRY_DELAY_MS } from '../config/constants';

/**
 * Options for retry behavior.
 */
export interface RetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxAttempts?: number;
  /** Base delay in milliseconds for exponential backoff (default: 1000) */
  baseDelay?: number;
  /** Maximum delay in milliseconds (default: 10000) */
  maxDelay?: number;
  /** Function to determine if error is retryable (default: all errors) */
  isRetryable?: (error: unknown) => boolean;
  /** Callback called before each retry */
  onRetry?: (attempt: number, error: unknown, delay: number) => void;
}

/**
 * Default function to determine if an error is retryable.
 * Network errors and 5xx status codes are retryable.
 */
export function isRetryableError(error: unknown): boolean {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    // Network error (fetch failed)
    return true;
  }

  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status;
    // Retry on server errors (5xx) and rate limiting (429)
    return status >= 500 || status === 429;
  }

  // Don't retry client errors (4xx except 429)
  return false;
}

/**
 * Calculate delay for exponential backoff with jitter.
 *
 * @param attempt - Current attempt number (1-indexed)
 * @param baseDelay - Base delay in milliseconds
 * @param maxDelay - Maximum delay cap
 * @returns Delay in milliseconds
 */
export function calculateBackoffDelay(
  attempt: number,
  baseDelay: number,
  maxDelay: number,
): number {
  // Exponential backoff: baseDelay * 2^(attempt-1)
  const exponentialDelay = baseDelay * Math.pow(2, attempt - 1);

  // Add jitter (±25%) to prevent thundering herd
  const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);

  return Math.min(exponentialDelay + jitter, maxDelay);
}

/**
 * Sleep for specified milliseconds.
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Execute an async function with exponential backoff retry.
 *
 * @param fn - Async function to execute
 * @param options - Retry configuration options
 * @returns Result of the function
 * @throws Last error if all retries fail
 *
 * @example
 * ```typescript
 * const result = await withRetry(
 *   () => fetch('/api/wallet'),
 *   {
 *     maxAttempts: 3,
 *     onRetry: (attempt, error) => console.log(`Retry ${attempt}:`, error)
 *   }
 * );
 * ```
 */
export async function withRetry<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
  const {
    maxAttempts = MAX_RETRY_ATTEMPTS,
    baseDelay = RETRY_BASE_DELAY_MS,
    maxDelay = MAX_RETRY_DELAY_MS,
    isRetryable = isRetryableError,
    onRetry,
  } = options;

  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry if not retryable or last attempt
      if (!isRetryable(error) || attempt === maxAttempts) {
        throw error;
      }

      // Calculate delay and wait
      const delay = calculateBackoffDelay(attempt, baseDelay, maxDelay);

      // Call retry callback if provided
      onRetry?.(attempt, error, delay);

      await sleep(delay);
    }
  }

  // This should never be reached, but TypeScript needs it
  throw lastError;
}

/**
 * Create a retryable version of a fetch function.
 *
 * @param fetchFn - The fetch function to wrap
 * @param options - Retry options
 * @returns Wrapped fetch function with retry logic
 *
 * @example
 * ```typescript
 * const fetchWithRetry = createRetryableFetch(fetch);
 * const response = await fetchWithRetry('/api/wallet');
 * ```
 */
export function createRetryableFetch(
  fetchFn: typeof fetch = fetch,
  options: RetryOptions = {},
): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    return withRetry(async () => {
      const response = await fetchFn(input, init);

      // Throw on error status codes so they can be retried
      if (!response.ok && isRetryableError({ status: response.status })) {
        throw { status: response.status, statusText: response.statusText };
      }

      return response;
    }, options);
  };
}

export default withRetry;
